import type { FC } from 'react';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { PapiGallection } from '@wix/da-papi-types';
import SearchIcon from '@wix/da-ds/pkg/Icons/24x24/Search';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import IconButton from '@wix/da-shared-react/pkg/Button/IconButton';
import FolderBarFolderDropdown from '../FolderBar/_partials/FolderBarFolderDropdown';
import WidgetHeaderMobile from '../../../UserWidgets/_partials/WidgetHeaderMobile';
import MobileSearchField from '../MobileSearchField';
import GalleryOrderDropdown from '../FolderBar/_partials/GalleryOrderDropdown/GalleryOrderDropdown';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks/useFlag';
import { GallectionType } from '@wix/da-shared-react/pkg/types/gallection';
import { ALL_FOLDER_ID } from '@wix/da-url';
import {
  isCurrentUserProfileOwner,
  getProfileOwnerUser,
} from '../../../../selectors/users';
import { getIsGroup } from '@wix/da-shared-react/pkg/User/helpers';

import profilesCssVars from '../../../../styles/variables.scss';
import s from './MobileGallectionHeader.scss';

interface Props {
  currentFolder?: PapiGallection;
  searchQuery?: string;
  resultsTotal?: number;
  disableSearch?: boolean;
}

const MobileGallectionHeader: FC<Props> = ({
  currentFolder,
  searchQuery,
  resultsTotal,
  disableSearch = false,
}) => {
  const { t } = useTranslation();
  const [showSearchBar, setShowSearchBar] = useState(!!searchQuery);
  const hasForyouGalleryFlag = useFlag('foryou_gallery');

  const isOwner = useSelector(isCurrentUserProfileOwner);
  const profileOwner = useSelector(getProfileOwnerUser);
  const isGroup = getIsGroup(profileOwner);

  const showGalleryOrderDropdown =
    hasForyouGalleryFlag &&
    !isOwner &&
    !isGroup &&
    !searchQuery &&
    currentFolder?.folderId === ALL_FOLDER_ID &&
    currentFolder?.type === GallectionType.GALLERY;

  return (
    <div className={s['root']}>
      {!disableSearch && showSearchBar && (
        <div className={s['above-header']}>
          <MobileSearchField />
        </div>
      )}
      <WidgetHeaderMobile
        isSticky={false}
        rootClassName={s['widget-header-mobile']}
        header={
          searchQuery ? (
            t('pages.gallections.search.mobile.resultsCount', {
              count: resultsTotal,
            })
          ) : (
            <FolderBarFolderDropdown
              offsetTop={parseInt(profilesCssVars['mobile-navbar-height']) - 2}
              currentFolder={currentFolder}
            />
          )
        }
        extras={
          <div className={s['extras']}>
            {showGalleryOrderDropdown && (
              <GalleryOrderDropdown className={s['gallery-order-dropdown']} />
            )}
            {!disableSearch && (
              <IconButton
                className={s['search-button']}
                onClick={() => setShowSearchBar(!showSearchBar)}
              >
                <SearchIcon size={IconSize.MEDIUM} />
              </IconButton>
            )}
          </div>
        }
      />
    </div>
  );
};
MobileGallectionHeader.displayName = 'MobileGallectionHeader';

export default MobileGallectionHeader;
